import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext'; // Updated import path
import type { Project, TeamName, Item, FundEntry, PurchaserFund, ProjectMode, ProjectStatus } from '../types/core';
import type { Attachment } from '../types/attachments';
import { createAuditEntry, removeUndefinedValues, generateItemId, type AuditEntry } from '../utils'; // Correct import for AuditEntry
import { AuditService } from '../services/AuditService'; // Import AuditService
import { db } from '../firebaseConfig';
import { doc, setDoc } from "firebase/firestore";

interface CreateProjectModalProps {
  onClose: () => void;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ onClose, setProjects }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [initialFund, setInitialFund] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<TeamName[]>(currentUser?.team ? [currentUser.team] : ['Marketing A']);
  const teamOptions: TeamName[] = ['Marketing A', 'Marketing B', 'Marketing C', 'Marketing D', 'Marketing E', 'Production SBK', 'Production SDC'];

  const handleCreate = async () => {
    if (!name.trim() || selectedTeams.length === 0 || !currentUser) return;
    try {
      const fundAmount = parseFloat(initialFund) || 0;
      const projectId = generateItemId();
      const auditEntry = createAuditEntry(currentUser.id, currentUser.fullName, 'PROJECT_CREATED', `Project "${name.trim()}" created.`);
      const fundEntries: FundEntry[] = fundAmount > 0 ? [{
        id: generateItemId(),
        amount: fundAmount,
        description: 'Initial Fund',
        date: new Date(),
        attachments: [],
        createdAt: new Date(),
        purchaserId: currentUser.id,
        type: 'initial_project'
      }] : [];

      const firestoreData: {
        name: string; description: string; initialFund: number; totalReceived: number; items: Item[]; fundEntries: FundEntry[]; purchaserFunds: PurchaserFund[];
        initialFundAttachments: Attachment[]; mode: ProjectMode; status: ProjectStatus; teamsInvolved: TeamName[]; createdBy: string; createdAt: string;
        auditTrail: AuditEntry[]; categories: string[]; lastUpdated: string; lastUpdatedBy: string; initialFundDate?: string; deadline?: string;
        deletionApprovals: Array<any>; deletionInitiatedBy?: string;
      } = {
        name: name.trim(), description: description.trim(), initialFund: fundAmount, totalReceived: fundAmount, items: [], fundEntries, purchaserFunds: [],
        initialFundAttachments: [], mode: 'รายการ', status: 'active', teamsInvolved: selectedTeams, createdBy: currentUser.id, createdAt: new Date().toISOString(),
        auditTrail: [], categories: ['อุปกรณ์สำนักงาน', 'อุปกรณ์', 'วัสดุ', 'บริการ', 'เดินทาง', 'การตลาด', 'การบำรุงรักษา', 'อื่นๆ'], // Init empty auditTrail
        lastUpdated: new Date().toISOString(), lastUpdatedBy: currentUser.id,
        deletionApprovals: [],
      };
      if (fundAmount > 0) firestoreData.initialFundDate = new Date().toISOString();
      if (deadline && deadline.trim()) firestoreData.deadline = deadline.trim();

      const cleanedData = removeUndefinedValues(firestoreData);
      const projectDocRef = doc(db, 'projects', projectId.toString());
      await setDoc(projectDocRef, cleanedData);


      // Add initial audit entry to subcollection
      await AuditService.addAuditEntry(projectId.toString(), auditEntry);
      // Remove manual state update since real-time listener will handle it
      onClose(); // Call onClose passed from props
      alert('Project created successfully!');
    } catch (error) { alert('Failed to create project. Please try again.'); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">{t('project.create')}</h2>
        <div className="space-y-4">
          <div><label className="block text-sm font-medium mb-1">{t('project.name')} *</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Enter project name" /></div>
          <div><label className="block text-sm font-medium mb-1">{t('project.description')}</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ height: '5rem' }} placeholder="Enter project description (optional)" /></div>
          <div><label className="block text-sm font-medium mb-1">{t('project.initial.budget')}</label><input type="number" value={initialFund} onChange={(e) => setInitialFund(e.target.value)} step="0.01" placeholder="0.00" min="0" /></div>
          <div><label className="block text-sm font-medium mb-1">{t('project.deadline')}</label><input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">{t('project.teams.involved')} *</label><div className="space-y-2 border rounded p-2 create-project-teams-select-box">{teamOptions.map(team => (<label key={team} className="flex items-center"><input type="checkbox" checked={selectedTeams.includes(team)} onChange={(e) => { if (e.target.checked) setSelectedTeams(prev => [...prev, team]); else setSelectedTeams(prev => prev.filter(t => t !== team)); }} className="mr-2" />{team}</label>))}</div>{selectedTeams.length === 0 && (<p className="text-red-600 text-sm mt-1">Please select at least one team</p>)}</div>
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button onClick={onClose} className="btn-secondary px-4 py-2">{t('action.cancel')}</button>
          <button onClick={handleCreate} disabled={!name.trim() || selectedTeams.length === 0} className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed">{t('project.create')}</button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
